import { InfoCircleOutlined } from '@ant-design/icons'
import { normalizeDispatchDate } from '@components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { useWizardMaster } from '@components/OrderWizard/Contexts/WizardMasterContext'
import { GraviButton, Horizontal, MomentDatePicker, Texto, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Select, Switch } from 'antd'
import type moment from 'moment-timezone'
import { useEffect } from 'react'

import type { CarrierWindowInputMode } from './useCarrierWindowOverride'
import { getShowNetworkTimezone } from '@/utils/general'

const { RangePicker } = MomentDatePicker

const timeOptions = new Array(24).fill(0).map((_, i) => {
  return {
    value: i,
    label: `${(i < 12 ? i : i - 12) || 12}:00 ${i < 12 ? 'AM' : 'PM'}`,
  }
})

type CarrierWindowOverrideProps = {
  inputMode: CarrierWindowInputMode
  setInputMode: (mode: CarrierWindowInputMode) => void
  isOverrideToggled: boolean
  onOverrideToggled: (value: boolean) => void
  customWindow: moment.Moment[] | undefined
  setCustomWindow: (
    _: moment.Moment[],
    dates: [string, string],
    timezone: Intl.DateTimeFormatOptions['timeZone']
  ) => void
  isInputDisabled?: boolean
  isToggleDisabled?: boolean
  simpleWindow: {
    date?: moment.Moment
    startTime?: number
    endTime?: number
    timezone: Intl.DateTimeFormatOptions['timeZone']
  }
  setSimpleWindow: (params: {
    date?: moment.Moment
    startTime?: number
    endTime?: number
    timezone: Intl.DateTimeFormatOptions['timeZone']
  }) => void
  clearWindow: () => void
  removeDatesFromPayload: () => void
  canEditCarrierWindow: boolean
  timezone: Intl.DateTimeFormatOptions['timeZone']
}

export function CarrierWindowOverride({
  inputMode,
  setInputMode,
  isOverrideToggled,
  onOverrideToggled,
  customWindow,
  setCustomWindow,
  isInputDisabled,
  isToggleDisabled,
  simpleWindow,
  setSimpleWindow,
  clearWindow,
  removeDatesFromPayload,
  canEditCarrierWindow,
  timezone,
}: CarrierWindowOverrideProps) {
  const { wizardOrder } = useWizardMaster()
  const { carrier_window_start: defaultStart, carrier_window_end: defaultEnd } = wizardOrder
  const hasExistingWindow = defaultStart && defaultEnd

  if (!canEditCarrierWindow || wizardOrder.state === 'complete') {
    if (hasExistingWindow) {
      return (
        <Vertical className='bg-theme2 py-1 px-2' flex='0' style={{ overflow: 'visible' }}>
          <Texto align='center' appearance='white' category='h5'>
            Carrier Window: {`${normalizeDispatchDate(defaultStart)} - ${normalizeDispatchDate(defaultEnd)}`}
          </Texto>
        </Vertical>
      )
    }
    return null
  }
  const blurEndInput = () => {
    // Safe way to access the picker DOM element

    const pickerElement = document.querySelector('.ant-picker-range')
    const endInput = pickerElement?.querySelectorAll('.ant-picker-input')?.[1]
    if (endInput instanceof HTMLElement) {
      endInput.blur()
    }
  }

  const showLocationTime = getShowNetworkTimezone()
  const getDefaultCustomWindow = () => {
    if (!customWindow) return undefined

    return showLocationTime
      ? (customWindow?.map((date) => date?.clone().tz(timezone ?? '')) as [moment.Moment, moment.Moment])
      : (customWindow as [moment.Moment, moment.Moment])
  }

  const getDefaultSimpleWindow = () => {
    if (!showLocationTime || !simpleWindow.date) {
      return simpleWindow
    }
    if (!timezone) return simpleWindow

    const originalDate = simpleWindow.date.clone()
    const startDateTime = originalDate
      .clone()
      .hour(simpleWindow?.startTime ?? 0)
      .minute(0)
      .second(0)
    const endDateTime = originalDate
      .clone()
      .hour(simpleWindow?.endTime ?? 0)
      .minute(0)
      .second(0)

    const convertedStart = startDateTime.tz(timezone ?? '')
    const convertedEnd = endDateTime.tz(timezone ?? '')

    return {
      startTime: convertedStart.hour(),
      endTime: convertedEnd.hour(),
      date: convertedStart.clone().startOf('day'), // Use the converted date
      timezone: timezone,
    }
  }

  useEffect(() => {
    if (showLocationTime && simpleWindow.date) {
      const offsetWindow = getDefaultSimpleWindow()
      setSimpleWindow(offsetWindow)
    }
  }, [showLocationTime, timezone])

  return (
    <Vertical flex='0' style={{ overflow: 'visible' }}>
      <Horizontal justifyContent='space-between' alignItems='center' bg='primary' border='all' className='py-1 px-3'>
        <Texto category='h5'>On this window, you can manually override the carrier window.</Texto>

        <Horizontal alignItems='center' style={{ overflow: 'visible' }}>
          <Texto category='h5' className='mr-3'>
            Manually manage window:
          </Texto>
          <Switch
            className='mr-3'
            checked={isOverrideToggled}
            onChange={onOverrideToggled}
            disabled={isToggleDisabled}
          />

          {isOverrideToggled ? (
            <Horizontal style={{ gap: 8 }} alignItems='center'>
              {inputMode === 'simple' ? (
                <>
                  <MomentDatePicker
                    format={(d) => formatters.DATE(d, timezone)}
                    allowClear={false}
                    disabled={isInputDisabled}
                    defaultValue={getDefaultSimpleWindow().date}
                    onChange={(date) => {
                      setSimpleWindow({ ...simpleWindow, date, timezone })
                    }}
                    style={{ minWidth: 120 }}
                  />
                  <Select
                    defaultValue={getDefaultSimpleWindow().startTime}
                    value={simpleWindow.startTime}
                    onChange={(startTime) => setSimpleWindow({ ...simpleWindow, startTime, timezone })}
                    options={timeOptions.map((o, i) => ({ ...o, disabled: i > 22 }))}
                    placeholder='Select a start time'
                    disabled={isInputDisabled}
                    style={{ minWidth: 120 }}
                  />
                  <Select
                    defaultValue={getDefaultSimpleWindow().endTime}
                    value={simpleWindow.endTime}
                    onChange={(endTime) => setSimpleWindow({ ...simpleWindow, endTime, timezone })}
                    options={timeOptions?.map((option) => ({
                      ...option,
                      disabled: option.value <= (simpleWindow.startTime ?? -1),
                    }))}
                    placeholder='Select an end time'
                    disabled={isInputDisabled}
                    style={{ minWidth: 120 }}
                  />
                </>
              ) : (
                <RangePicker
                  format='MM/DD/YYYY h:mmA'
                  onCalendarChange={(dates, dateStrings) => {
                    if (dates?.[0] && dates?.[1] && dates[0].isAfter(dates[1])) {
                      return
                    }
                    if (dates?.[0] && dates?.[1] && dates[1].isBefore(dates[1])) {
                      return
                    }
                    setCustomWindow(dates, dateStrings, timezone)
                    if (dates?.[0] && !dates?.[1]) {
                      blurEndInput()
                    }
                  }}
                  showTime={{ format: 'h:mmA' }}
                  disabled={isInputDisabled}
                  allowClear={false}
                  defaultValue={getDefaultCustomWindow()}
                  onChange={(_, dates) => setCustomWindow(_, dates, timezone)}
                />
              )}

              <GraviButton
                onClick={() => {
                  setInputMode(inputMode === 'simple' ? 'custom' : 'simple')
                  // clearWindow()
                  removeDatesFromPayload()
                }}
              >
                <InfoCircleOutlined className='pr-1' />
                <i>Use {inputMode === 'simple' ? 'Custom' : 'Simple'} Window</i>
              </GraviButton>
            </Horizontal>
          ) : hasExistingWindow ? (
            <Horizontal>
              <Texto category='h5'>{`${normalizeDispatchDate(defaultStart, false, timezone)} - ${normalizeDispatchDate(
                defaultEnd,
                false,
                timezone
              )}`}</Texto>
            </Horizontal>
          ) : null}
        </Horizontal>
      </Horizontal>
    </Vertical>
  )
}
